/**
 * ReviewStep — final step of the ABR filter form dialog.
 * Displays a read-only summary of the filter settings before submission.
 * Label filter sections are hidden when source = reported content.
 */

import type { LabelFilterInput } from '@moderation/domain/AbrFilter';
import { FilterOperator } from '@moderation/domain/AbrFilter';
import { Card, Heading, Text } from '@soteria/design-system-ve';
import type { StandardLabel } from '@system-config/label-management/domain';

import * as styles from './AbrFilterFormDialog.css';

export interface ReviewStepProps {
  name: string;
  description: string;
  reportedContent: boolean;
  textLabelFilters: LabelFilterInput[];
  textLabelFilterModifier: FilterOperator;
  imageLabelFilters: LabelFilterInput[];
  imageLabelFilterModifier: FilterOperator;
  standardLabels: StandardLabel[];
}

function getLabelName(labelId: string, standardLabels: StandardLabel[]): string {
  return standardLabels.find(l => l.id === labelId)?.labelName ?? labelId;
}

function modifierLabel(modifier: FilterOperator): string {
  return modifier === FilterOperator.And ? 'AND (all must match)' : 'OR (any must match)';
}

export function ReviewStep({
  name,
  description,
  reportedContent,
  textLabelFilters,
  textLabelFilterModifier,
  imageLabelFilters,
  imageLabelFilterModifier,
  standardLabels,
}: ReviewStepProps) {
  const showLabelFilters = !reportedContent;

  return (
    <div className={styles.stepContentStyles}>
      <Heading level="h3">Review Queue Filter</Heading>

      <Card elevation="low" padding="medium">
        <div className={styles.fieldGroupStyles}>
          <Text className={styles.fieldLabelStyles}>Name</Text>
          <Text>{name}</Text>
        </div>

        <div className={`${styles.fieldGroupStyles} ${styles.reviewSectionSpacing}`}>
          <Text className={styles.fieldLabelStyles}>Description</Text>
          <Text>{description || '—'}</Text>
        </div>

        <div className={`${styles.fieldGroupStyles} ${styles.reviewSectionSpacing}`}>
          <Text className={styles.fieldLabelStyles}>Source</Text>
          <Text>{reportedContent ? 'Reported content' : 'Classified content'}</Text>
        </div>

        {showLabelFilters && (
          <>
            <div className={`${styles.fieldGroupStyles} ${styles.reviewSectionSpacing}`}>
              <Text className={styles.fieldLabelStyles}>Text Label Filters</Text>
              {textLabelFilters.length === 0 ? (
                <Text>None</Text>
              ) : (
                <>
                  {textLabelFilters.length > 1 && <Text>Modifier: {modifierLabel(textLabelFilterModifier)}</Text>}
                  <div className={styles.labelChipListStyles}>
                    {textLabelFilters.map(f => (
                      <Text key={f.labelId} className={styles.labelChipStyles}>
                        {getLabelName(f.labelId, standardLabels)} — min severity: {f.minSeverity ?? '—'}
                      </Text>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className={`${styles.fieldGroupStyles} ${styles.reviewSectionSpacing}`}>
              <Text className={styles.fieldLabelStyles}>Image Label Filters</Text>
              {imageLabelFilters.length === 0 ? (
                <Text>None</Text>
              ) : (
                <>
                  {imageLabelFilters.length > 1 && <Text>Modifier: {modifierLabel(imageLabelFilterModifier)}</Text>}
                  <div className={styles.labelChipListStyles}>
                    {imageLabelFilters.map(f => (
                      <Text key={f.labelId} className={styles.labelChipStyles}>
                        {getLabelName(f.labelId, standardLabels)} — min score: {f.minScore ?? '—'}
                      </Text>
                    ))}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
